/**
 * AppFactory - Templates via Firestore SDK (no Turing API, no 401)
 * Same pattern as deviceTypeNormalizationStore, deviceTypeRegistryStore.
 */

import { collection, doc, getDoc, getDocs, setDoc, deleteDoc } from 'firebase/firestore'
import { assertFirestoreEnvironmentForWrite, auth, firestore } from './firebase'
import { encryptCredential, decryptCredential } from './credentialCrypto'
import { listTemplatePresets, getTemplatePreset } from '../data/appsuite-template-presets'

const COLLECTION = 'appfactory_template_overrides'
const COMPONENT_STYLE_COLLECTION = 'appfactory_component_style_overrides'
/** Device type modal overrides 存於 component_style_overrides，doc ID 前綴 device-modal-，與 Component Styles 共用同一 collection 與規則 */
const DEVICE_MODAL_PREFIX = 'device-modal-'

function getOverridesRef() {
  if (!firestore) return null
  return collection(firestore, COLLECTION)
}

function getOverrideDocRef(templateId: string) {
  if (!firestore) return null
  return doc(firestore, COLLECTION, templateId)
}

function getComponentStyleOverridesRef() {
  if (!firestore) return null
  return collection(firestore, COMPONENT_STYLE_COLLECTION)
}

function getComponentStyleOverrideDocRef(styleKey: string) {
  if (!firestore) return null
  return doc(firestore, COMPONENT_STYLE_COLLECTION, styleKey)
}

export type Template = {
  id: string
  name: string
  description?: string
  status: 'draft' | 'published'
  type: string
  createdAt?: string
  updatedAt?: string
  version?: string
}

export type TemplatePreset = {
  presetId: string
  name: string
  type: string
  description?: string
  sections?: Record<string, unknown>
  styles?: { tokens?: Record<string, unknown> }
}

export async function listTemplates(): Promise<Template[]> {
  const presets = listTemplatePresets()
  const overridesRef = getOverridesRef()
  if (!overridesRef) {
    return presets.map((p) => ({
      id: p.presetId,
      name: p.name,
      description: p.description,
      status: 'draft' as const,
      type: p.type,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }))
  }
  const snap = await getDocs(overridesRef)
  const overridesByDoc = new Map<string, { name?: string; updatedAt?: string }>()
  snap.docs.forEach((d) => overridesByDoc.set(d.id, d.data() as { name?: string; updatedAt?: string }))
  return presets.map((p) => {
    const ov = overridesByDoc.get(p.presetId) || {}
    return {
      id: p.presetId,
      name: ov.name ?? p.name,
      description: p.description,
      status: 'draft' as const,
      type: p.type,
      createdAt: new Date().toISOString(),
      updatedAt: ov.updatedAt || new Date().toISOString(),
    }
  })
}

export async function getTemplate(id: string): Promise<Template | null> {
  const preset = getTemplatePreset(id)
  if (!preset) return null
  const ref = getOverrideDocRef(id)
  let overrides: { name?: string; updatedAt?: string } = {}
  if (ref) {
    const snap = await getDoc(ref)
    if (snap.exists()) overrides = snap.data() as { name?: string; updatedAt?: string }
  }
  return {
    id: preset.presetId,
    name: overrides.name ?? preset.name,
    description: preset.description,
    status: 'draft',
    type: preset.type,
    createdAt: new Date().toISOString(),
    updatedAt: overrides.updatedAt || new Date().toISOString(),
  }
}

export async function updateTemplateName(id: string, name: string): Promise<{ success: boolean; error?: string }> {
  assertFirestoreEnvironmentForWrite('updateTemplateName')
  const preset = getTemplatePreset(id)
  if (!preset) return { success: false, error: 'Preset not found' }
  const ref = getOverrideDocRef(id)
  if (!ref) return { success: false, error: 'Firestore not available' }
  const user = auth?.currentUser
  try {
    const snap = await getDoc(ref)
    const current = snap.exists() ? (snap.data() as Record<string, unknown>) : {}
    await setDoc(
      ref,
      {
        ...current,
        name: name.trim(),
        updatedAt: new Date().toISOString(),
        updatedBy: user?.uid || 'system',
      },
      { merge: true }
    )
    return { success: true }
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'Failed' }
  }
}

export async function getTemplatePresetWithOverrides(id: string): Promise<TemplatePreset | null> {
  const preset = getTemplatePreset(id)
  if (!preset) return null
  const ref = getOverrideDocRef(id)
  if (!ref) return { ...preset, presetId: preset.presetId }
  const snap = await getDoc(ref)
  const overrides = snap.exists() ? (snap.data() as { sections?: Record<string, unknown> }) : null
  if (overrides?.sections) {
    return {
      ...preset,
      presetId: preset.presetId,
      sections: { ...(preset.sections || {}), ...overrides.sections },
    }
  }
  return { ...preset, presetId: preset.presetId }
}

export type TemplateSections = {
  card?: {
    defaultConfig?: Record<string, unknown>
    deviceTypes?: Record<string, unknown>
  }
  modal?: {
    defaultConfig?: Record<string, unknown>
    deviceTypes?: Record<string, Record<string, unknown>>
  }
  login?: {
    styleId?: string
  }
  editor?: {
    templateItemCss?: Record<string, string>
    templateItemFramerPreset?: Record<string, string>
  }
}

export async function updateTemplateSections(
  id: string,
  sections: TemplateSections
): Promise<{ success: boolean; error?: string }> {
  assertFirestoreEnvironmentForWrite('updateTemplateSections')
  const preset = getTemplatePreset(id)
  if (!preset) return { success: false, error: 'Preset not found' }
  const ref = getOverrideDocRef(id)
  if (!ref) return { success: false, error: 'Firestore not available' }
  const user = auth?.currentUser
  try {
    const snap = await getDoc(ref)
    const currentSections = (snap.exists() ? (snap.data()?.sections as Record<string, unknown>) : {}) || {}
    const merged = { ...currentSections }
    for (const [key, value] of Object.entries(sections)) {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        merged[key] = { ...(merged[key] as Record<string, unknown> || {}), ...value }
      } else {
        merged[key] = value
      }
    }
    await setDoc(
      ref,
      {
        sections: merged,
        updatedAt: new Date().toISOString(),
        updatedBy: user?.uid || 'system',
      },
      { merge: true }
    )
    return { success: true }
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'Failed' }
  }
}

export async function listComponentStyleNameOverrides(): Promise<Record<string, string>> {
  const ref = getComponentStyleOverridesRef()
  if (!ref) return {}
  const snap = await getDocs(ref)
  const result: Record<string, string> = {}
  for (const d of snap.docs) {
    const data = d.data() as { styleName?: unknown }
    const styleName = String(data?.styleName || '').trim()
    if (!styleName) continue
    result[d.id] = styleName
  }
  return result
}

export async function updateComponentStyleName(
  styleKey: string,
  styleName: string
): Promise<{ success: boolean; error?: string }> {
  assertFirestoreEnvironmentForWrite('updateComponentStyleName')
  const key = String(styleKey || '').trim()
  const name = String(styleName || '').trim()
  if (!key) return { success: false, error: 'styleKey is required' }
  if (!name) return { success: false, error: 'styleName is required' }
  const ref = getComponentStyleOverrideDocRef(key)
  if (!ref) return { success: false, error: 'Firestore not available' }
  const user = auth?.currentUser
  try {
    await setDoc(ref, {
      styleName: name,
      updatedAt: new Date().toISOString(),
      updatedBy: user?.uid || 'system',
    }, { merge: true })
    return { success: true }
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'Failed' }
  }
}

/** Frame preset for per-component outer frame/background */
export type FramePreset = 'default' | 'Glass' | 'GlassV2' | 'LiquidGlass' | 'Solid' | 'Minimal' | 'FrostedLight'

/** Device type modal overrides: styleName, styleId, styleDescription, mockDeviceName, modal config */
export type DeviceTypeModalOverride = {
  styleName?: string
  styleId?: string
  styleDescription?: string
  mockDeviceName?: string
  /** 如 ['trait:OnOff', 'trait:FanSpeed', 'state:temperature'] */
  modalItemOrder?: string[]
  /** trait -> rawStyleId，如 { OnOff: 'Style01', TemperatureSetting: 'Style05' } */
  modalTraitStyles?: Record<string, string>
  /** stateKey -> rawStyleId，如 { temperature: 'Style01' } */
  modalStateStyles?: Record<string, string>
  /** chartId -> rawStyleId，如 { TemperatureChart: 'Style01' } */
  modalChartStyles?: Record<string, string>
  /** itemKey -> 是否顯示，如 { 'trait:OnOff': true, 'state:temperature': false }，預設 true */
  modalItemVisible?: Record<string, boolean>
  /** itemKey -> 與下一個元件的間距 (px)，如 { 'trait:OnOff': 20 }，預設 20 */
  modalItemSpacing?: Record<string, number>
  /** itemKey -> frame preset，如 { 'trait:OnOff': 'Glass', 'state:temperature': 'Solid' }，預設 'default' 使用元件內建 */
  modalFrameStyles?: Record<string, FramePreset>
  /** itemKey -> CSS 片段，如 { 'trait:OnOff': '.foo { color: #fff; }' } */
  modalItemCss?: Record<string, string>
  /** Modal 整體背景：'transparent' 或顏色值如 'rgba(255,255,255,0.06)' */
  modalBackground?: 'transparent' | string
}

/** AppFactory credentials stored in appfactory_component_style_overrides (same rules as Component Styles) */
const USER_CREDENTIAL_DOC_PREFIX = 'user-credentials-'

function getUserCredentialDocRef(uid: string) {
  if (!firestore) return null
  return doc(firestore, COMPONENT_STYLE_COLLECTION, USER_CREDENTIAL_DOC_PREFIX + uid)
}

export async function getCredentialFromUserProfile(): Promise<{ appId: string; apiKey: string } | null> {
  const user = auth?.currentUser
  if (!user?.uid || !firestore) return null
  const ref = getUserCredentialDocRef(user.uid)
  if (!ref) return null
  try {
    const snap = await getDoc(ref)
    const data = snap.exists() ? (snap.data() as { appId?: string; apiKey?: string; encrypted?: boolean }) : null
    if (!data?.appId && !data?.apiKey) return null
    let appId = data.appId ?? ''
    let apiKey = data.apiKey ?? ''
    if (data.encrypted) {
      if (appId) appId = await decryptCredential(appId)
      if (apiKey) apiKey = await decryptCredential(apiKey)
    }
    return appId || apiKey ? { appId, apiKey } : null
  } catch {
    return null
  }
}

export async function setCredentialInUserProfile(
  appId: string,
  apiKey: string
): Promise<{ success: boolean; error?: string }> {
  assertFirestoreEnvironmentForWrite('setCredentialInUserProfile')
  const user = auth?.currentUser
  if (!user?.uid) return { success: false, error: 'Not authenticated' }
  const ref = getUserCredentialDocRef(user.uid)
  if (!ref) return { success: false, error: 'Firestore not available' }
  try {
    const credCrypto = await import('./credentialCrypto')
    const isEnc = credCrypto.isEncryptionEnabled()
    const payload: Record<string, unknown> = {
      updatedAt: new Date().toISOString(),
      updatedBy: user.uid,
    }
    if (isEnc) {
      payload.appId = await credCrypto.encryptCredential(appId)
      payload.apiKey = await credCrypto.encryptCredential(apiKey)
      payload.encrypted = true
    } else {
      payload.appId = appId
      payload.apiKey = apiKey
      payload.encrypted = false
    }
    await setDoc(ref, payload, { merge: true })
    return { success: true }
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'Failed' }
  }
}

export async function clearCredentialFromUserProfile(): Promise<{ success: boolean; error?: string }> {
  assertFirestoreEnvironmentForWrite('clearCredentialFromUserProfile')
  const user = auth?.currentUser
  if (!user?.uid) return { success: false, error: 'Not authenticated' }
  const ref = getUserCredentialDocRef(user.uid)
  if (!ref) return { success: false, error: 'Firestore not available' }
  try {
    await deleteDoc(ref)
    return { success: true }
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'Failed' }
  }
}

/** Doc ID = device-modal-{canonicalStyleId}，存於 appfactory_component_style_overrides（與 Component Styles 共用規則） */
function getDeviceTypeModalOverrideDocRef(canonicalStyleId: string) {
  if (!firestore) return null
  const key = DEVICE_MODAL_PREFIX + String(canonicalStyleId || '').trim()
  return doc(firestore, COMPONENT_STYLE_COLLECTION, key)
}

export async function listDeviceTypeModalOverrides(): Promise<Record<string, DeviceTypeModalOverride>> {
  if (!firestore) return {}
  const ref = getComponentStyleOverridesRef()
  if (!ref) return {}
  const snap = await getDocs(ref)
  const result: Record<string, DeviceTypeModalOverride> = {}
  const prefix = DEVICE_MODAL_PREFIX
  for (const d of snap.docs) {
    if (!d.id.startsWith(prefix)) continue
    const canonicalStyleId = d.id.slice(prefix.length)
    const data = d.data() as DeviceTypeModalOverride
    result[canonicalStyleId] = {
      styleName: data?.styleName,
      styleId: data?.styleId,
      styleDescription: data?.styleDescription,
      mockDeviceName: data?.mockDeviceName,
      modalItemOrder: data?.modalItemOrder,
      modalTraitStyles: data?.modalTraitStyles,
      modalStateStyles: data?.modalStateStyles,
      modalChartStyles: data?.modalChartStyles,
      modalItemVisible: data?.modalItemVisible,
      modalItemSpacing: data?.modalItemSpacing,
      modalFrameStyles: data?.modalFrameStyles,
      modalItemCss: data?.modalItemCss,
      modalBackground: data?.modalBackground,
    }
  }
  return result
}

export async function updateDeviceTypeModalOverride(
  canonicalStyleId: string,
  payload: Partial<DeviceTypeModalOverride>
): Promise<{ success: boolean; error?: string }> {
  assertFirestoreEnvironmentForWrite('updateDeviceTypeModalOverride')
  const ref = getDeviceTypeModalOverrideDocRef(canonicalStyleId)
  if (!ref) return { success: false, error: 'Firestore not available' }
  const user = auth?.currentUser
  try {
    // Firestore 不接受 undefined 欄位；clone/edit payload 可能帶入 undefined
    const sanitizedPayload = Object.fromEntries(
      Object.entries(payload).filter(([, v]) => v !== undefined)
    ) as Partial<DeviceTypeModalOverride>
    await setDoc(ref, {
      ...sanitizedPayload,
      updatedAt: new Date().toISOString(),
      updatedBy: user?.uid || 'system',
    }, { merge: true })
    return { success: true }
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'Failed' }
  }
}

export async function deleteDeviceTypeModalOverride(
  canonicalStyleId: string
): Promise<{ success: boolean; error?: string }> {
  assertFirestoreEnvironmentForWrite('deleteDeviceTypeModalOverride')
  const ref = getDeviceTypeModalOverrideDocRef(canonicalStyleId)
  if (!ref) return { success: false, error: 'Firestore not available' }
  try {
    await deleteDoc(ref)
    return { success: true }
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'Failed' }
  }
}

/** Component Styles 工作區自訂 CSS，doc ID = appfactory-custom-css */
const CUSTOM_CSS_DOC_ID = 'appfactory-custom-css'

/** Framer preset 視覺編輯器覆蓋，doc ID = appfactory-framer-overrides */
const FRAMER_OVERRIDES_DOC_ID = 'appfactory-framer-overrides'

/** Login Page 視覺編輯器覆蓋（與 Framer 相同結構），doc ID = appfactory-login-page-overrides */
const LOGIN_PAGE_OVERRIDES_DOC_ID = 'appfactory-login-page-overrides'

export async function getCustomCss(): Promise<string> {
  if (!firestore) return ''
  const ref = doc(firestore, COMPONENT_STYLE_COLLECTION, CUSTOM_CSS_DOC_ID)
  const snap = await getDoc(ref)
  const data = snap.exists() ? (snap.data() as { css?: string }) : {}
  return String(data?.css ?? '').trim()
}

export async function updateCustomCss(css: string): Promise<{ success: boolean; error?: string }> {
  assertFirestoreEnvironmentForWrite('updateCustomCss')
  if (!firestore) return { success: false, error: 'Firestore not available' }
  const ref = doc(firestore, COMPONENT_STYLE_COLLECTION, CUSTOM_CSS_DOC_ID)
  const user = auth?.currentUser
  try {
    await setDoc(ref, {
      css: String(css ?? ''),
      updatedAt: new Date().toISOString(),
      updatedBy: user?.uid || 'system',
    }, { merge: true })
    return { success: true }
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'Failed' }
  }
}

/** Framer preset 視覺編輯器覆蓋：{ [preset]: FrameStyleVars } */
export type FramerPresetOverrides = Record<string, {
  '--frame-bg'?: string
  '--frame-border'?: string
  '--frame-shadow'?: string
  '--frame-radius'?: string
  '--frame-blur'?: string
  '--frame-padding'?: string
}>

export async function getFramerPresetOverrides(): Promise<FramerPresetOverrides> {
  if (!firestore) return {}
  const ref = doc(firestore, COMPONENT_STYLE_COLLECTION, FRAMER_OVERRIDES_DOC_ID)
  const snap = await getDoc(ref)
  const data = snap.exists() ? (snap.data() as { overrides?: FramerPresetOverrides }) : {}
  return data?.overrides ?? {}
}

export async function updateFramerPresetOverride(
  preset: string,
  vars: FramerPresetOverrides[string]
): Promise<{ success: boolean; error?: string }> {
  assertFirestoreEnvironmentForWrite('updateFramerPresetOverride')
  if (!firestore) return { success: false, error: 'Firestore not available' }
  const ref = doc(firestore, COMPONENT_STYLE_COLLECTION, FRAMER_OVERRIDES_DOC_ID)
  const user = auth?.currentUser
  try {
    const snap = await getDoc(ref)
    const current = snap.exists() ? (snap.data() as { overrides?: FramerPresetOverrides }) : {}
    const overrides = { ...(current?.overrides ?? {}), [preset]: vars }
    await setDoc(ref, {
      overrides,
      updatedAt: new Date().toISOString(),
      updatedBy: user?.uid || 'system',
    }, { merge: true })
    return { success: true }
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'Failed' }
  }
}

export async function deleteFramerPresetOverride(preset: string): Promise<{ success: boolean; error?: string }> {
  assertFirestoreEnvironmentForWrite('deleteFramerPresetOverride')
  if (!firestore) return { success: false, error: 'Firestore not available' }
  if (preset === 'default') return { success: false, error: 'Cannot delete default preset' }
  const ref = doc(firestore, COMPONENT_STYLE_COLLECTION, FRAMER_OVERRIDES_DOC_ID)
  const user = auth?.currentUser
  try {
    const snap = await getDoc(ref)
    const current = snap.exists() ? (snap.data() as { overrides?: FramerPresetOverrides }) : {}
    const overrides = { ...(current?.overrides ?? {}) }
    delete overrides[preset]
    await setDoc(ref, {
      overrides,
      updatedAt: new Date().toISOString(),
      updatedBy: user?.uid || 'system',
    }, { merge: true })
    return { success: true }
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'Failed' }
  }
}

/** Login Page style 視覺編輯器覆蓋：{ [styleId]: FrameStyleVars }，結構與 Framer 相同 */
export type LoginPagePresetOverrides = Record<string, FramerPresetOverrides[string]>

export async function getLoginPagePresetOverrides(): Promise<LoginPagePresetOverrides> {
  if (!firestore) return {}
  const ref = doc(firestore, COMPONENT_STYLE_COLLECTION, LOGIN_PAGE_OVERRIDES_DOC_ID)
  const snap = await getDoc(ref)
  const data = snap.exists() ? (snap.data() as { overrides?: LoginPagePresetOverrides }) : {}
  return data?.overrides ?? {}
}

export async function updateLoginPagePresetOverride(
  styleId: string,
  vars: LoginPagePresetOverrides[string]
): Promise<{ success: boolean; error?: string }> {
  assertFirestoreEnvironmentForWrite('updateLoginPagePresetOverride')
  if (!firestore) return { success: false, error: 'Firestore not available' }
  const ref = doc(firestore, COMPONENT_STYLE_COLLECTION, LOGIN_PAGE_OVERRIDES_DOC_ID)
  const user = auth?.currentUser
  try {
    const snap = await getDoc(ref)
    const current = snap.exists() ? (snap.data() as { overrides?: LoginPagePresetOverrides }) : {}
    const overrides = { ...(current?.overrides ?? {}), [styleId]: vars }
    await setDoc(ref, {
      overrides,
      updatedAt: new Date().toISOString(),
      updatedBy: user?.uid || 'system',
    }, { merge: true })
    return { success: true }
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'Failed' }
  }
}

export async function deleteLoginPagePresetOverride(styleId: string): Promise<{ success: boolean; error?: string }> {
  assertFirestoreEnvironmentForWrite('deleteLoginPagePresetOverride')
  if (!firestore) return { success: false, error: 'Firestore not available' }
  const ref = doc(firestore, COMPONENT_STYLE_COLLECTION, LOGIN_PAGE_OVERRIDES_DOC_ID)
  const user = auth?.currentUser
  try {
    const snap = await getDoc(ref)
    const current = snap.exists() ? (snap.data() as { overrides?: LoginPagePresetOverrides }) : {}
    const overrides = { ...(current?.overrides ?? {}) }
    delete overrides[styleId]
    await setDoc(ref, {
      overrides,
      updatedAt: new Date().toISOString(),
      updatedBy: user?.uid || 'system',
    }, { merge: true })
    return { success: true }
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'Failed' }
  }
}

// ---------------------------------------------------------------------------
// Design Tokens (Phase 5)
// ---------------------------------------------------------------------------

/** Design Tokens 存於 component_style_overrides，doc ID = appfactory-design-tokens */
const DESIGN_TOKENS_DOC_ID = 'appfactory-design-tokens'

export interface DesignTokens {
  colors: Record<string, string>
  radius?: Record<string, string>
  spacing?: Record<string, string>
}

export async function getDesignTokens(): Promise<DesignTokens> {
  if (!firestore) return { colors: {} }
  const ref = doc(firestore, COMPONENT_STYLE_COLLECTION, DESIGN_TOKENS_DOC_ID)
  const snap = await getDoc(ref)
  const data = snap.exists() ? (snap.data() as { tokens?: DesignTokens }) : {}
  return data?.tokens ?? { colors: {} }
}

export async function updateDesignTokens(tokens: DesignTokens): Promise<{ success: boolean; error?: string }> {
  assertFirestoreEnvironmentForWrite('updateDesignTokens')
  if (!firestore) return { success: false, error: 'Firestore not available' }
  const ref = doc(firestore, COMPONENT_STYLE_COLLECTION, DESIGN_TOKENS_DOC_ID)
  try {
    await setDoc(ref, {
      tokens,
      updatedAt: new Date().toISOString(),
      updatedBy: auth?.currentUser?.uid || 'system',
    }, { merge: true })
    return { success: true }
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'Failed' }
  }
}
